import { useState, useEffect } from 'react';
import { api } from '../services/api';

interface ActiveProvider {
  provider_name: string;
  display_name: string;
  default_model: string | null;
  models: string[];
}

interface ModelSelectorProps {
  value?: { provider: string; model: string } | null;
  onChange: (selection: { provider: string; model: string }) => void;
  className?: string;
  compact?: boolean;
}

const PROVIDER_COLORS: Record<string, string> = {
  anthropic: 'text-orange-600',
  openai: 'text-green-600',
  google: 'text-blue-600',
  mistral: 'text-purple-600',
  groq: 'text-red-600',
};

export default function ModelSelector({ value, onChange, className = '', compact = false }: ModelSelectorProps) {
  const [providers, setProviders] = useState<ActiveProvider[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get('/llm-settings/active-providers')
      .then(res => {
        setProviders(res.data);
        // If no value set yet, default to first provider's default model
        if (!value && res.data.length > 0) {
          const first = res.data[0];
          onChange({ provider: first.provider_name, model: first.default_model || first.models[0] });
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!loaded || providers.length === 0) return null;

  const selectedProvider = value?.provider || providers[0]?.provider_name;
  const currentProv = providers.find(p => p.provider_name === selectedProvider) || providers[0];
  const selectedModel = value?.model || currentProv?.default_model || currentProv?.models[0];

  if (compact) {
    // Single-line compact view
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <select
          value={`${selectedProvider}::${selectedModel}`}
          onChange={e => {
            const [prov, model] = e.target.value.split('::');
            onChange({ provider: prov, model });
          }}
          className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-brand-500"
        >
          {providers.map(p => (
            <optgroup key={p.provider_name} label={p.display_name}>
              {p.models.map(m => (
                <option key={`${p.provider_name}::${m}`} value={`${p.provider_name}::${m}`}>
                  {m}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <label className="text-xs font-medium text-gray-500 whitespace-nowrap">AI Model:</label>
      <select
        value={selectedProvider}
        onChange={e => {
          const prov = providers.find(p => p.provider_name === e.target.value);
          if (prov) onChange({ provider: prov.provider_name, model: prov.default_model || prov.models[0] });
        }}
        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-brand-500"
      >
        {providers.map(p => (
          <option key={p.provider_name} value={p.provider_name}>{p.display_name}</option>
        ))}
      </select>
      <select
        value={selectedModel}
        onChange={e => onChange({ provider: selectedProvider, model: e.target.value })}
        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-brand-500"
      >
        {currentProv?.models.map(m => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
      <span className={`text-xs font-medium ${PROVIDER_COLORS[selectedProvider] || 'text-gray-500'}`}>
        {currentProv?.display_name}
      </span>
    </div>
  );
}
